const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OrderResponse = require('@/lib/Orders/OrderResponse');
const { apiResponseToError } = require('@/lib/Api');

/**
 * Takes an OrderRequest and makes API requests to create an order from it, creating a reservation along the way.
 *
 * This is used for submitting offline orders (OrderRequests that were stored in the OnlineQueue).
 * It is also used when you press the "Send To Kitchen" button from the Kitchen modal.
 */
class OrderRequestSubmitter {
    /**
     * @param {Hydrator} hydrator
     * @param {OrderAdjustmentConfirmer} orderAdjustmentConfirmer
     * @param orderResource
     * @param {ReservationService} reservationService
     */
    constructor(
        hydrator,
        orderAdjustmentConfirmer,
        orderResource,
        reservationService
    ) {
        this.hydrator = hydrator;
        this.orderAdjustmentConfirmer = orderAdjustmentConfirmer;
        this.orderResource = orderResource;
        this.reservationService = reservationService;

        this.log = logFactory('OrderRequestSubmitter');
    }

    /**
     * @param {OrderRequest} orderRequest
     *
     * @returns {Promise<OrderResponse|OrderAdjustmentResponse>}
     */
    async submit(orderRequest) {
        this.log.debug('Submitting order request', orderRequest);

        if (orderRequest.order.reservationToken) {
            try {
                await this.reservationService.deleteReservation(
                    orderRequest.order.reservationToken
                );
            } catch (err) {
                // Ignore errors deleting the existing reservation. We're going to make a new one anyway.
            }
            orderRequest.order.reservationToken = null;
        }

        // Create a reservation. Save the token to the orderRequest.
        await this.createReservation(orderRequest);

        if (orderRequest.order.adjustedOrderID) {
            return this.orderAdjustmentConfirmer.sendAdjustmentCreationRequest(orderRequest.order);
        }

        return this.createOrder(orderRequest);
    }

    /**
     * @param {OrderRequest} orderRequest
     *
     * @returns {Promise}
     */
    createReservation(orderRequest) {
        return this.reservationService
            .putReservation(orderRequest.reservation)
            .then((reservation) => {
                orderRequest.order.reservationToken = reservation.token;

                this.log.debug('Successfully created reservation', reservation);

                return reservation;
            })
            .catch((err) => {
                this.log.error('Failed to create reservation from order request', orderRequest, err);
                throw err;
            });
    }

    /**
     * @param {OrderRequest} orderRequest
     *
     * @returns {Promise<OrderResponse>}
     */
    createOrder(orderRequest) {
        return new Promise((resolve, reject) => {
            // The confirmation structure used to include the payments, but now it doesn't.
            // Add those back in here rather than sending a separate request to add payments.
            // Maybe later we'll do 2 requests.
            const requestData = {
                ...orderRequest.order,

                /**
                 * Note these payments are already converted to PaymentForApiStructure in the OrderRequest.
                 *
                 * @type {PaymentForApiStructure[]}
                 */
                payments: orderRequest.payments,
            };

            this.orderResource.confirm(
                {
                    // If onlineTaskTrigger exists add it to the query params.
                    ...(orderRequest.order.onlineTaskTrigger ? { onlineTaskTrigger: orderRequest.order.onlineTaskTrigger } : {}),
                },
                requestData,
                (response) => {
                    let res = this.hydrator.hydrate(
                        response,
                        new OrderResponse()
                    );

                    this.log.log('Successfully created order', orderRequest, response);

                    resolve(res);
                },
                (response) => {
                    this.log.error('Failed to create order from order request', orderRequest, response);

                    reject(apiResponseToError(response));
                }
            );
        });
    }
}

module.exports = OrderRequestSubmitter;
